import React, { useEffect, useMemo } from 'react';
import { Card, Image, Typography, Flex, Tooltip } from 'antd';
import { RightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CustomButton from '../../../components/CustomButton/Button';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import NoData from "../../../components/NoData/NoData"
import { getAllInterestStatus, getDealsByStatus } from '../../../redux/slices/DealSlice';
import { calculateSoldOutPercentage, calculateReturns, formatToLacCr, calculateAvailableAmountToInvest } from '../../../helpers/helpers';

const { Title, Text } = Typography;

const ALLOWED_CATEGORY_NAMES = new Set([
  'jointventure',
  'jointventures',
  'invoicefinancing',
  'supplychainfinance',
  'supplychainfinancing',
]);

const normalizeCategory = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');

const INFO_LABELS = {
  returns: 'Estimated annual returns for this investment opportunity.',
  maturity: 'Estimated duration to completion/maturity of this deal.',
  dealValue: 'Total funding requirement for this business/deal.',
  remainingAmount: 'Available amount open for investors before the deal is fully funded.',
};

const renderMetricLabel = (label, tooltip) => (
  <span className='deal-metric-label-with-info'>
    {label}
    <Tooltip title={tooltip} placement='top'>
      <InfoCircleOutlined className='deal-metric-info-icon' />
    </Tooltip>
  </span>
);

const isJointVenturesDeal = (deal) => {
  const category = normalizeCategory(deal?.dealCategoryName);
  return category === 'jointventure' || category === 'jointventures';
};

const getDealBadge = (deal) => {
  const category = normalizeCategory(deal?.dealCategoryName);
  if (category === 'jointventure' || category === 'jointventures') {
    return { label: 'Joint Ventures', className: 'jv-badge' };
  }
  if (category === 'invoicefinancing') {
    return { label: 'Invoice Financing', className: 'invoice-badge' };
  }
  if (category === 'supplychainfinance' || category === 'supplychainfinancing') {
    return { label: 'Supply Chain Financing', className: 'scf-badge' };
  }
  return { label: '', className: '' };
};

const parsePercentValue = (rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === '') return null;
  const normalized = String(rawValue).replace(/[%\s,]/g, '').trim();
  if (!normalized) return null;

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;

  return parsed <= 1 ? Number((parsed * 100).toFixed(2)) : Number(parsed.toFixed(2));
};

const getDealSortDate = (deal) => {
  const preferredDate = deal?.dealApprovedDate || deal?.createdAt || deal?.dealCreatedDate || deal?.updatedAt;
  const parsedTime = new Date(preferredDate).getTime();
  return Number.isNaN(parsedTime) ? 0 : parsedTime;
};

const RecommendedDeals = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dealsData, interestStatus } = useSelector((state) => state.deal);

  useEffect(() => {
    const payload = { status: "approved" };
    dispatch(getDealsByStatus(payload));
    dispatch(getAllInterestStatus());
  }, [dispatch]);

  const getDealAmount = (approvedFundAmt, fundedAmt) => {
    let initDealAmount = 200000
    const availableAmountToInvest = calculateAvailableAmountToInvest(approvedFundAmt, fundedAmt);
    if (availableAmountToInvest <= 200000) {
      initDealAmount = availableAmountToInvest;
    }
    return initDealAmount
  }

  const getJointVentureDetails = (deal) => {
    const expectedRoi = parsePercentValue(deal?.jvDealExpectedROI ?? deal?.expectedROI);
    const profitShare = parsePercentValue(deal?.profitShare ?? deal?.jvDealProfitShare);
    const remainingAmount = Number(calculateAvailableAmountToInvest(deal?.approvedFundAmt || 0, deal?.fundedAmt || 0));

    return {
      expectedRoi,
      profitShare,
      remainingAmount,
    };
  };

  const recommendedDeals = useMemo(() => {
    if (!Array.isArray(dealsData) || dealsData.length === 0) {
      return [];
    }

    return dealsData
      .filter((deal) => ALLOWED_CATEGORY_NAMES.has(normalizeCategory(deal?.dealCategoryName)))
      .sort((a, b) => getDealSortDate(b) - getDealSortDate(a))
      .slice(0, 3);
  }, [dealsData]);


  return (
    <div className='recommended-deals-container'>
      <div className='deals-header-container'>
        <Title level={4}>{`Top 3 Recommended Deals for you`}</Title>
      </div>
      {recommendedDeals.length > 0 ? (
        recommendedDeals.map((deal) => {
          const isJVDeal = isJointVenturesDeal(deal);
          const jvDetails = getJointVentureDetails(deal);
          const dealBadge = getDealBadge(deal);

          return (
            <Card key={deal.dealId} className={`recommended-deal-card ${isJVDeal ? 'jv-recommended-deal-card' : ''}`}>
              <Flex align='start' justify='space-between' wrap>
                <Flex align='top' gap={0} className='deal-title-row'>
                  {!isJVDeal && deal?.entLogo ? <Image src={deal.entLogo} alt='Ent Logo' height={'auto'} width={'30px'} preview={false} className='ent-logo-image' /> : null}
                  <div className='deal-title-content'>
                    <Title level={5}>
                      {isJVDeal
                        ? (deal?.dealName || deal?.dealTitle || deal?.entName || 'Deal')
                        : (deal?.entName || deal?.dealName || deal?.dealTitle || 'Deal')}
                    </Title>
                  </div>
                </Flex>
                <div className='deal-header-tags'>
                  {dealBadge.label ? <span className={`deal-category-badge ${dealBadge.className}`}>{dealBadge.label}</span> : null}
                  <div className='sold-out-tag'>{`${calculateSoldOutPercentage(deal?.fundedAmt, deal?.approvedFundAmt)}% Sold Out`}</div>
                </div>
              </Flex>

              {isJVDeal ? (
                <div className='jv-deal-body'>
                  <div className='ent-description' style={{ width: '85%' }}>
                    <Text style={{ marginBottom: 0, fontSize: 12 }}>
                      {deal?.entDescription || deal?.dealDescription || ''}
                    </Text>
                  </div>

                  <div className='deal-footer-actions-container'>
                    <div className='deal-constraint-container jv-constraint-container'>
                      <div className='deal-constraint-details'>
                        <Text style={{ fontSize: 12, color: '#40566d' }}>{renderMetricLabel('Expected ROI', INFO_LABELS.returns)}</Text>
                        <Title level={5}>{`${jvDetails.expectedRoi ?? 0}%`}</Title>
                      </div>
                      <div className='deal-constraint-details'>
                        <Text style={{ fontSize: 12, color: '#40566d' }}>Profit Share</Text>
                        <Title level={5}>{`${jvDetails.profitShare ?? 0}%`}</Title>
                      </div>
                      <div className='deal-constraint-details'>
                        <Text style={{ fontSize: 12, color: '#40566d' }}>{renderMetricLabel('Deal Value', INFO_LABELS.dealValue)}</Text>
                        <Title level={5}>{`₹ ${formatToLacCr(Number(deal?.approvedFundAmt || 0))}`}</Title>
                      </div>
                    </div>

                    <CustomButton
                      size='small'
                      variant='text'
                      color='primary'
                      icon={<RightOutlined />}
                      iconPosition='end'
                      style={{ fontSize: 12, color: '#fff', background: 'rgba(255, 122, 26, 1)', padding: '18px 12px' }}
                      onClick={() => navigate('/deal-details', {
                        state: {
                          dealId: deal?.dealId,
                          enteredDealAmount: getDealAmount(deal?.approvedFundAmt, deal?.fundedAmt),
                          options: interestStatus
                        },
                      })}
                    >
                      View Deal Details
                    </CustomButton>
                  </div>
                </div>
              ) : (
                <>
                  <div className='ent-description' style={{ width: '85%' }}>
                    <Text style={{ marginBottom: 0, fontSize: 12 }}>
                      {deal?.entDescription || deal?.businessDescription || deal?.dealDescription || ''}
                    </Text>
                  </div>
                  <div className='deal-footer-actions-container'>
                    <div className='deal-constraint-container'>
                      <div className='deal-constraint-details'>
                        <Text style={{ fontSize: 12, color: '#40566d' }}>{renderMetricLabel('Returns', INFO_LABELS.returns)}</Text>
                        <Title level={5}>{calculateReturns(deal?.annualDiscountRate)}</Title>
                      </div>
                      <div className='deal-constraint-details'>
                        <Text style={{ fontSize: 12, color: '#40566d' }}>{renderMetricLabel('Maturity', INFO_LABELS.maturity)}</Text>
                        <Title level={5}>{deal?.tenure} Days</Title>
                      </div>
                      <div className='deal-constraint-details'>
                        <Text style={{ fontSize: 12, color: '#40566d' }}>{renderMetricLabel('Deal Value', INFO_LABELS.dealValue)}</Text>
                        <Title level={5}>₹ {formatToLacCr(Number(deal?.approvedFundAmt))}</Title>
                      </div>
                      <div className='deal-constraint-details'>
                        <Text style={{ fontSize: 12, color: '#40566d' }}>{renderMetricLabel('Remaining amount to invest', INFO_LABELS.remainingAmount)}</Text>
                        <Title level={5}>₹ {formatToLacCr(Number(calculateAvailableAmountToInvest(deal?.approvedFundAmt || 0, deal?.fundedAmt || 0)))}</Title>
                      </div>
                    </div>
                    <CustomButton
                      size='small'
                      variant='text'
                      color='primary'
                      icon={<RightOutlined />}
                      iconPosition='end'
                      style={{ fontSize: 12, color: '#fff', background: 'rgba(255, 122, 26, 1)', padding: '18px 12px' }}
                      onClick={() => navigate('/deal-details', {
                        state: {
                          dealId: deal?.dealId,
                          enteredDealAmount: getDealAmount(deal?.approvedFundAmt, deal?.fundedAmt),
                        },
                      })}
                    >
                      View Deal Details
                    </CustomButton>
                  </div>
                </>
              )}
            </Card>
          );
        })
      ) : (
        <NoData />
      )}
    </div>
  );
}

export default RecommendedDeals;
